import {
  Box,
  Container,
  Text,
  Title,
  Button,
  Group,
  Stack,
  SimpleGrid,
  Badge,
  Accordion,
} from '@mantine/core';
import { motion } from 'framer-motion';
import { useNavigate, Link } from 'react-router-dom';
import { SEO } from '../components/shared/SEO';
import { trackCTAClick, trackLinkClick } from '../analytics';
import {
  IconBrain,
  IconGlobe,
  IconBrandGmail,
  IconCalendarEvent,
  IconBrandGoogleDrive,
  IconTable,
  IconForms,
  IconAddressBook,
  IconListCheck,
  IconBrandTelegram,
  IconBrandWhatsapp,
  IconBrandDiscord,
  IconBrandNotion,
  IconBrandGithub,
  IconBrandTrello,
  IconArrowRight,
  IconMessage,
  IconMessageCircle,
  IconMoodSmile,
  IconReceipt,
  IconClock,
  IconAssembly,
  IconSparkles,
  IconShoppingCart,
  IconSearch,
  IconShare,
  IconChecklist,
  IconChartBar,
  IconReportAnalytics,
  IconPalette,
} from '@tabler/icons-react';

const CSS = `
  @keyframes gradient-shift {
    0%   { background-position: 0% 50%; }
    50%  { background-position: 100% 50%; }
    100% { background-position: 0% 50%; }
  }
  @keyframes float {
    0%,100% { transform: translate(0,0) scale(1); }
    33%     { transform: translate(40px,-30px) scale(1.05); }
    66%     { transform: translate(-20px,20px) scale(0.97); }
  }
  @keyframes pulse-ring {
    0%   { transform: scale(1); opacity: .3; }
    50%  { transform: scale(1.15); opacity: .08; }
    100% { transform: scale(1); opacity: .3; }
  }
  .hero-gradient-text {
    background: linear-gradient(135deg, #7c3aed 0%, #6366f1 40%, #3b82f6 100%);
    background-size: 200% 200%;
    animation: gradient-shift 6s ease infinite;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }
  .card-hover {
    transition: all .3s cubic-bezier(.4,0,.2,1);
    height: 100%;
    position: relative;
    overflow: hidden;
  }
  .card-hover::before {
    content: '';
    position: absolute;
    top: 0; left: 0; right: 0;
    height: 2px;
    background: linear-gradient(90deg, transparent, #7c3aed, transparent);
    opacity: 0;
    transition: opacity .3s ease;
  }
  .card-hover:hover {
    transform: translateY(-4px);
    box-shadow: 0 20px 40px rgba(0,0,0,.06);
    border-color: rgba(124,58,237,.15) !important;
  }
  .card-hover:hover::before { opacity: 1; }
  .task-card:hover {
    background: rgba(124,58,237,.08) !important;
    transform: translateX(4px);
  }
  .task-card:hover .task-card-icon { color: #c4b5fd !important; }
  .task-card:hover .task-card-text { color: rgba(255,255,255,.85) !important; }
  .hero-cta {
    transition: all .3s cubic-bezier(.4,0,.2,1) !important;
  }
  .hero-cta:hover {
    transform: translateY(-2px) !important;
    box-shadow: 0 8px 32px rgba(0,0,0,.15) !important;
  }
  .soon-tag {
    background: linear-gradient(135deg, #f5f3ff, #ede9fe);
    color: #7c3aed;
    border: 1px solid rgba(124,58,237,.12);
  }
  .transparency-item {
    transition: all .2s ease;
  }
  .transparency-item:hover {
    transform: translateY(-2px);
    background: rgba(124,58,237,.04) !important;
  }
  @keyframes marquee {
    0% { transform: translateX(0); }
    100% { transform: translateX(-50%); }
  }
  .marquee-track {
    animation: marquee 30s linear infinite;
  }
  .marquee-track:hover {
    animation-play-state: paused;
  }
  .marquee-track-reverse {
    animation: marquee 35s linear infinite reverse;
  }
  .marquee-track-reverse:hover {
    animation-play-state: paused;
  }
  @media (max-width: 768px) {
    .integration-card {
      padding: 12px 16px !important;
      gap: 10px !important;
      border-radius: 12px !important;
    }
    .integration-card-icon {
      width: 40px !important;
      height: 40px !important;
      border-radius: 10px !important;
    }
    .integration-card-icon svg {
      width: 20px !important;
      height: 20px !important;
    }
    .integration-card-text {
      font-size: 13px !important;
    }
    .marquee-gap {
      gap: 16px !important;
    }
    .marquee-wrapper {
      padding: 20px 0 !important;
    }
  }
`;

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: (i: number = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, delay: i * 0.1, ease: [0.25, 0.4, 0.25, 1] as [number, number, number, number] },
  }),
};

const VP = { once: true, margin: '-60px' as any };

const CHANNELS = [
  { label: 'Telegram', icon: IconBrandTelegram },
  { label: 'WhatsApp', icon: IconBrandWhatsapp, soon: true },
  { label: 'Signal', icon: IconMessageCircle, soon: true },
  { label: 'Discord', icon: IconBrandDiscord, soon: true },
  { label: 'Web chat', icon: IconMessageCircle, soon: true },
];

const INTEGRATIONS = [
  { label: 'Gmail', icon: IconBrandGmail },
  { label: 'Google Calendar', icon: IconCalendarEvent },
  { label: 'Google Drive', icon: IconBrandGoogleDrive },
  { label: 'Google Sheets', icon: IconTable },
  { label: 'Google Forms', icon: IconForms },
  { label: 'Google Contacts', icon: IconAddressBook },
  { label: 'Google Tasks', icon: IconListCheck },
  { label: 'Telegram', icon: IconBrandTelegram },
  { label: 'Notion', icon: IconBrandNotion },
  { label: 'GitHub', icon: IconBrandGithub },
  { label: 'Todoist', icon: IconChecklist },
  { label: 'Trello', icon: IconBrandTrello },
];

const PERSONALITY_ITEMS = [
  { label: 'Deep memory that grows with you', desc: 'Remembers everything. Context that grows.', icon: IconBrain },
  { label: 'Customizable tone & style', desc: 'Professional, casual, supportive \u2014 pick your vibe.', icon: IconPalette },
  { label: 'Defined role', desc: 'Executive assistant, research buddy, or your own custom role.', icon: IconMoodSmile },
];

const OTHER_CAPABILITIES = [
  { label: 'Schedule tasks', icon: IconClock },
  { label: 'Usage cost breakdown', icon: IconReceipt },
  { label: 'Web browsing', icon: IconGlobe },
  { label: 'Data analysis', icon: IconChartBar },
  { label: 'Reporting', icon: IconReportAnalytics },
];

const STATS = [
  { value: '24/7', label: 'Uptime' },
  { value: '<5m', label: 'To Build' },
  { value: '100%', label: 'Isolated' },
  { value: 'Zero', label: 'Code Required' },
];

const FAQ_ITEMS = [
  {
    q: 'What is Manifestus?',
    a: 'A managed platform for building autonomous AI Manifestants. Compose a Manifestant from modular building blocks \u2014 tools, integrations, and personality \u2014 without writing code or managing servers.',
  },
  {
    q: 'What is a Manifestant?',
    a: 'A Manifestant is your personal AI that manifests your intents. You define its role, personality, and capabilities \u2014 and it acts on your behalf: scheduling tasks, browsing the web, sending reminders, and more. Think of it as an autonomous extension of yourself.',
  },
  {
    q: 'How is Manifestus different from ChatGPT?',
    a: 'ChatGPT is a general chat interface \u2014 it responds when you ask. Manifestants are autonomous: they schedule tasks, run recurring routines, and act on your behalf even while you sleep. They integrate directly with Gmail, Calendar, Drive, Notion, GitHub, and Telegram \u2014 not just chat. And you get full observability on every action and cost.',
  },
  {
    q: 'Do I need technical skills to use Manifestus?',
    a: 'No. You configure your Manifestant by toggling tools and integrations. No coding, no config files, no servers.',
  },
  {
    q: 'How do I talk to my Manifestant?',
    a: 'Today you can use Telegram. Soon we\u2019ll add WhatsApp, Signal, Discord, and web chat. Pick the channel that fits your workflow.',
  },
  {
    q: 'How much does Manifestus cost?',
    a: 'Free to start. Full cost observability included \u2014 see what every task costs, broken down by category, with optimization suggestions.',
    link: '/pricing',
  },
  {
    q: 'Is my data safe?',
    a: 'Every Manifestant runs in a fully isolated environment. Your data never touches other users. You control what your Manifestant can access. Every action is logged in a full audit trail.',
  },
  {
    q: 'What can I connect Manifestus to?',
    a: 'Gmail, Google Calendar, Drive, Sheets, Forms, Contacts, Tasks, Notion, GitHub, Todoist, Trello, Telegram, and more. New integrations are added regularly.',
  },
  {
    q: 'Can I create and share custom Manifestants?',
    a: 'Yes, coming soon. The Manifestant Factory lets you build as many Manifestants as you want, each with its own expertise — no code required. The Marketplace lets you share them with the community or discover what others have built.',
  },
];

const FAQ_JSON_LD = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  'mainEntity': FAQ_ITEMS.map((item) => ({
    '@type': 'Question',
    'name': item.q,
    'acceptedAnswer': {
      '@type': 'Answer',
      'text': item.a,
    },
  })),
};

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <Text size="sm" fw={600} c="#7c3aed" mb={8} style={{ letterSpacing: '.06em', textTransform: 'uppercase' as const }}>
      {children}
    </Text>
  );
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <Title order={2} mb={20} style={{ fontSize: 'clamp(1.8rem, 4vw, 2.75rem)', fontWeight: 800, color: '#0a0a0a', letterSpacing: '-.02em', lineHeight: 1.15 }}>
      {children}
    </Title>
  );
}

export default function LandingPage() {
  const navigate = useNavigate();
  const go = () => navigate('/login');

  return (
    <Box component="main">
      <SEO
        title="AI that manifests yourself | Manifestus"
        description="Build AI Manifestants you can see, control, and trust. Transparent, modular, and ready in minutes. Handle the repetitive so you can focus on what you love."
        path="/"
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'WebApplication',
          'name': 'Manifestus',
          'url': 'https://manifestus.ai',
          'description': 'Build AI Manifestants you can see, control, and trust. Transparent, modular, ready in minutes.',
          'applicationCategory': 'BusinessApplication',
          'operatingSystem': 'Web',
          'offers': { '@type': 'Offer', 'price': '0', 'priceCurrency': 'EUR' },
          'creator': { '@type': 'Organization', 'name': 'Manifestus', 'url': 'https://manifestus.ai' },
        }}
      />
      <style>{CSS}</style>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(FAQ_JSON_LD) }} />

      {/* ═══════════════════════════════════════════════════
          CHAPTER 1: HERO
          ═══════════════════════════════════════════════════ */}
      <Box
        component="section"
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          overflow: 'hidden',
          paddingTop: 80,
          paddingBottom: 60,
        }}
      >
        <Box style={{ position: 'absolute', width: 600, height: 600, borderRadius: '50%', background: 'radial-gradient(circle, rgba(124,58,237,.06) 0%, transparent 70%)', top: '10%', right: '-10%', animation: 'float 20s ease-in-out infinite', pointerEvents: 'none' }} />
        <Box style={{ position: 'absolute', width: 500, height: 500, borderRadius: '50%', background: 'radial-gradient(circle, rgba(59,130,246,.04) 0%, transparent 70%)', bottom: '5%', left: '-5%', animation: 'float 25s ease-in-out infinite reverse', pointerEvents: 'none' }} />

        <Container size="sm" style={{ position: 'relative', zIndex: 1 }}>
          <Stack gap={28} align="center" ta="center">
            <motion.div variants={fadeUp} initial="hidden" animate="visible" custom={0}>
              <Title order={1} style={{ fontSize: 'clamp(2.8rem, 6vw, 4.5rem)', fontWeight: 800, lineHeight: 1.05, letterSpacing: '-.035em', color: '#0a0a0a' }}>
                AI that manifests
                <br />
                <span className="hero-gradient-text">yourself.</span>
              </Title>
            </motion.div>

            <motion.div variants={fadeUp} initial="hidden" animate="visible" custom={1}>
              <Text size="lg" maw={520} mx="auto" lh={1.7} style={{ color: '#64748b' }}>
                Build AI Manifestants that handle the repetitive, so you can
                focus on what you love. Transparent. Modular. Ready in minutes.
              </Text>
            </motion.div>

            <motion.div variants={fadeUp} initial="hidden" animate="visible" custom={2}>
              <Group gap="md" justify="center" mt={4}>
                <Button
                  size="lg" color="dark" radius="xl" className="hero-cta"
                  rightSection={<IconArrowRight size={18} />}
                  onClick={() => { trackCTAClick('Start Building', 'hero', '/login'); go(); }}
                  style={{ fontWeight: 600, fontSize: 15, paddingInline: 28, height: 48 }}
                >
                  Start Building
                </Button>
                <Button
                  size="lg" variant="default" radius="xl"
                  component="a" href="#how-it-works"
                  onClick={() => trackLinkClick('See How It Works', '#how-it-works', false)}
                  style={{ fontWeight: 500, fontSize: 15, height: 48, paddingInline: 24, borderColor: '#e2e8f0', color: '#475569' }}
                >
                  See How It Works
                </Button>
              </Group>
            </motion.div>

            <motion.div variants={fadeUp} initial="hidden" animate="visible" custom={3}>
              <Text size="xs" c="#94a3b8">
                Free to start · No credit card · Ready in 5 minutes
              </Text>
            </motion.div>
          </Stack>
        </Container>
      </Box>

      {/* ═══════════════════════════════════════════════════
          CHAPTER 2: THE TENSION
          ═══════════════════════════════════════════════════ */}
      <Box component="section" py={{ base: 80, md: 120 }} style={{ borderTop: '1px solid rgba(0,0,0,.04)' }}>
        <Container size="lg">
          <SimpleGrid cols={{ base: 1, md: 2 }} spacing={56} verticalSpacing={40}>
            <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={VP} custom={0}>
              <Stack gap="lg" justify="center" style={{ height: '100%' }}>
                <SectionLabel>The problem</SectionLabel>
                <Title order={2} mb={20} style={{ fontSize: 'clamp(1.8rem, 3.5vw, 2.4rem)', fontWeight: 800, color: '#0a0a0a', letterSpacing: '-.02em', lineHeight: 1.15 }}>
                  With powerful tools come
                  <br />
                  <span style={{ color: '#94a3b8' }}>real responsibilities.</span>
                </Title>
                <Text size="md" c="#64748b" lh={1.75} maw={440}>
                  Most AI platforms are opaque. Manifestus shows you everything — visible, auditable, customizable, private, and under your control.
                </Text>
              </Stack>
            </motion.div>

            <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={VP} custom={1}>
              <Box style={{
                background: 'linear-gradient(145deg, #0f172a, #1e293b)',
                borderRadius: 20,
                border: '1px solid rgba(255,255,255,.06)',
                padding: 0,
                overflow: 'hidden',
                boxShadow: '0 25px 60px rgba(0,0,0,.15), 0 0 0 1px rgba(255,255,255,.05) inset',
              }}>
                {/* Window chrome */}
                <Box style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '14px 20px', borderBottom: '1px solid rgba(255,255,255,.06)' }}>
                  <Box style={{ width: 10, height: 10, borderRadius: '50%', background: '#ef4444', opacity: 0.8 }} />
                  <Box style={{ width: 10, height: 10, borderRadius: '50%', background: '#eab308', opacity: 0.8 }} />
                  <Box style={{ width: 10, height: 10, borderRadius: '50%', background: '#22c55e', opacity: 0.8 }} />
                  <Text size="xs" c="rgba(255,255,255,.3)" ml={8} style={{ fontFamily: 'monospace', fontSize: 11 }}>manifestus</Text>
                </Box>
                <Stack gap={0} style={{ padding: '8px 12px 12px' }}>
                  {[
                    { text: 'Schedule a meeting with Alex for Thursday afternoon' },
                    { text: 'Reconcile last month\u2019s expenses against the budget' },
                    { text: 'Research competitor pricing and put it in a Notion doc' },
                    { text: 'Follow up with every lead that went cold this quarter' },
                  ].map((msg, i) => (
                    <Box
                      key={i}
                      className="task-card"
                      style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 12,
                        padding: '14px 16px',
                        borderRadius: 10,
                        cursor: 'default',
                        transition: 'all .25s cubic-bezier(.4,0,.2,1)',
                      }}
                    >
                      <Box style={{ width: 28, height: 28, borderRadius: 8, background: 'rgba(124,58,237,.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <IconMessage size={14} className="task-card-icon" color="#a78bfa" stroke={1.5} style={{ transition: 'color .25s' }} />
                      </Box>
                      <Text size="sm" className="task-card-text" c="rgba(255,255,255,.6)" lh={1.5} style={{ transition: 'color .25s', marginTop: 3 }}>
                        {msg.text}
                      </Text>
                    </Box>
                  ))}
                </Stack>
              </Box>
            </motion.div>
          </SimpleGrid>
        </Container>
      </Box>

      {/* ═══════════════════════════════════════════════════
          CHAPTER 3: BUILT LIKE LEGO — SECTIONS
          ═══════════════════════════════════════════════════ */}
      <Box component="section" id="how-it-works" py={{ base: 80, md: 120 }}>
        <Container size="lg">
          <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={VP} custom={0}>
            <Stack align="center" ta="center" mb={64}>
              <SectionLabel>Built like Lego</SectionLabel>
              <SectionTitle>
                Four building blocks.
                <br />
                <span style={{ color: '#94a3b8' }}>Infinite combinations.</span>
              </SectionTitle>
              <Text size="md" c="#64748b" maw={460} lh={1.7}>
                Add what you need. Remove what you don{'\u2019'}t. Nothing hidden, nothing locked.
              </Text>
            </Stack>
          </motion.div>

          {/* ── Communication: compact pill strip ── */}
          <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={VP} custom={1}>
            <Stack gap={20} mb={64}>
              <Stack gap={4}>
                <Text fw={700} size="xl" c="#0a0a0a">Communication</Text>
                <Text size="sm" c="#94a3b8">Talk on the apps you already use</Text>
              </Stack>
              <SimpleGrid cols={{ base: 2, sm: 5 }} spacing="sm" verticalSpacing="sm">
                {CHANNELS.map((ch) => (
                  <Group key={ch.label} gap={10} justify="space-between" wrap="nowrap" style={{ padding: '10px 18px', borderRadius: 999, background: 'rgba(124,58,237,.06)', border: '1px solid rgba(124,58,237,.12)' }}>
                    <Group gap={10} wrap="nowrap" style={{ minWidth: 0 }}>
                      <ch.icon size={20} color="#7c3aed" stroke={1.5} style={{ flexShrink: 0 }} />
                      <Text size="sm" fw={500} c="#0a0a0a" truncate>{ch.label}</Text>
                    </Group>
                    {ch.soon && (
                      <Badge className="soon-tag" size="xs" radius="sm" variant="light" style={{ textTransform: 'none', fontWeight: 600, fontSize: 9, flexShrink: 0 }}>
                        Soon
                      </Badge>
                    )}
                  </Group>
                ))}
              </SimpleGrid>
            </Stack>
          </motion.div>

          {/* ── Integrations section (carousel) ── */}
          <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={VP} custom={2}>
            <Stack gap={24} mb={64}>
              <Stack gap={4}>
                <Text fw={700} size="xl" c="#0a0a0a">Integrations</Text>
                <Text size="sm" c="#94a3b8">Your apps, connected</Text>
              </Stack>
              <Box className="marquee-wrapper" style={{ overflow: 'hidden', padding: '28px 0', marginLeft: 0, marginRight: 0 }}>
                <Box className="marquee-track-reverse marquee-gap" style={{ display: 'flex', gap: 24, width: 'max-content' }}>
                  {[...INTEGRATIONS, ...INTEGRATIONS].map((int, i) => (
                    <Box
                      key={`${int.label}-${i}`}
                      className="integration-card"
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 14,
                        padding: '20px 24px',
                        borderRadius: 16,
                        background: 'rgba(255,255,255,.7)',
                        border: '1px solid rgba(0,0,0,.06)',
                        flexShrink: 0,
                        transition: 'transform .2s, box-shadow .2s',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'scale(1.03)';
                        e.currentTarget.style.boxShadow = '0 12px 32px rgba(0,0,0,.08)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = '';
                        e.currentTarget.style.boxShadow = '';
                      }}
                    >
                      <Box className="integration-card-icon" style={{ width: 56, height: 56, borderRadius: 14, background: 'linear-gradient(135deg, #f5f3ff, #ede9fe)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <int.icon size={28} color="#7c3aed" stroke={1.5} />
                      </Box>
                      <Text className="integration-card-text" fw={600} size="sm" c="#0a0a0a">{int.label}</Text>
                    </Box>
                  ))}
                </Box>
              </Box>
            </Stack>
          </motion.div>

          {/* ── Personality: vertical list in single block ── */}
          <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={VP} custom={3}>
            <Stack gap={20} mb={64}>
              <Stack gap={4}>
                <Text fw={700} size="xl" c="#0a0a0a">Personality</Text>
                <Text size="sm" c="#94a3b8">Tailored to you</Text>
              </Stack>
              <Box style={{ padding: 32, borderRadius: 16, background: 'linear-gradient(135deg, rgba(124,58,237,.04), rgba(99,102,241,.04))', border: '1px solid rgba(124,58,237,.1)' }}>
                <Stack gap={24}>
                  {PERSONALITY_ITEMS.map((p) => (
                    <Group key={p.label} align="flex-start" gap={20} wrap="nowrap">
                      <Box style={{ width: 40, height: 40, borderRadius: 10, background: 'rgba(255,255,255,.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, border: '1px solid rgba(124,58,237,.15)' }}>
                        <p.icon size={20} color="#7c3aed" stroke={1.5} />
                      </Box>
                      <Stack gap={2} style={{ flex: 1, minWidth: 0 }}>
                        <Text fw={600} size="sm" c="#0a0a0a">{p.label}</Text>
                        <Text size="xs" c="#64748b" lh={1.6}>{p.desc}</Text>
                      </Stack>
                    </Group>
                  ))}
                </Stack>
              </Box>
            </Stack>
          </motion.div>

          {/* ── Other capabilities: inline list with icons ── */}
          <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={VP} custom={4}>
            <Stack gap={20}>
              <Stack gap={4}>
                <Text fw={700} size="xl" c="#0a0a0a">Other capabilities</Text>
                <Text size="sm" c="#94a3b8">More than just chat</Text>
              </Stack>
              <SimpleGrid cols={{ base: 1, sm: 2, md: 5 }} spacing="md" verticalSpacing="md">
                {OTHER_CAPABILITIES.map((c) => (
                  <Group key={c.label} gap={8} wrap="nowrap">
                    <c.icon size={18} color="#7c3aed" stroke={1.5} style={{ flexShrink: 0, opacity: 0.9 }} />
                    <Text size="sm" fw={500} c="#475569">{c.label}</Text>
                  </Group>
                ))}
              </SimpleGrid>
            </Stack>
          </motion.div>
        </Container>
      </Box>

      {/* ═══════════════════════════════════════════════════
          CHAPTER 4: BUILDER + MARKETPLACE
          ═══════════════════════════════════════════════════ */}
      <Box component="section" py={{ base: 80, md: 120 }} style={{ borderTop: '1px solid rgba(0,0,0,.04)' }}>
        <Container size="lg">
          <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={VP} custom={0}>
            <Stack align="center" ta="center" mb={56}>
              <SectionLabel>Create & Share</SectionLabel>
              <SectionTitle>
                Build your own.
                <br />
                <span style={{ color: '#94a3b8' }}>Share with everyone.</span>
              </SectionTitle>
              <Text size="md" c="#64748b" maw={480} lh={1.7}>
                Create custom Manifestants, or discover community creations on the Marketplace.
              </Text>
            </Stack>
          </motion.div>

          <SimpleGrid cols={{ base: 1, md: 2 }} spacing={24}>
            {/* Builder Card */}
            <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={VP} custom={1}>
              <Box
                p={32}
                style={{
                  background: 'rgba(255,255,255,.7)',
                  backdropFilter: 'blur(8px)',
                  borderRadius: 20,
                  border: '1px solid rgba(0,0,0,.05)',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <Group justify="space-between" align="flex-start" mb={16}>
                  <Box style={{ width: 48, height: 48, borderRadius: 12, background: 'linear-gradient(135deg, #f5f3ff, #ede9fe)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <IconAssembly size={24} color="#7c3aed" stroke={1.5} />
                  </Box>
                  <Badge className="soon-tag" size="sm" radius="sm" variant="light" style={{ textTransform: 'none', fontWeight: 600, fontSize: 11 }}>
                    Coming Soon
                  </Badge>
                </Group>
                <Text fw={700} size="xl" c="#0a0a0a" mb={8}>Manifestant Factory</Text>
                <Text size="sm" c="#64748b" lh={1.7} mb={20}>
                  Build as many as you want — each with its own expertise. No code required.
                </Text>
                <Group gap={8} wrap="wrap" style={{ marginTop: 'auto' }}>
                  {['Sports Fan', 'Morning Strategist', 'Stock Analyst', 'Research Partner', 'Travel Planner', 'Personal Assistant', 'Pet Counsellor', 'Fashion Advisor'].map((skill) => (
                    <Badge
                      key={skill}
                      variant="light"
                      color="gray"
                      size="md"
                      radius="md"
                      style={{ textTransform: 'none', fontWeight: 500, background: '#f1f5f9', color: '#475569', border: '1px solid #e2e8f0' }}
                    >
                      {skill}
                    </Badge>
                  ))}
                </Group>
              </Box>
            </motion.div>

            {/* Marketplace Card */}
            <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={VP} custom={2}>
              <Box
                p={32}
                style={{
                  background: 'rgba(255,255,255,.7)',
                  backdropFilter: 'blur(8px)',
                  borderRadius: 20,
                  border: '1px solid rgba(0,0,0,.05)',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <Group justify="space-between" align="flex-start" mb={16}>
                  <Box style={{ width: 48, height: 48, borderRadius: 12, background: 'linear-gradient(135deg, #f5f3ff, #ede9fe)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <IconShoppingCart size={24} color="#7c3aed" stroke={1.5} />
                  </Box>
                  <Badge className="soon-tag" size="sm" radius="sm" variant="light" style={{ textTransform: 'none', fontWeight: 600, fontSize: 11 }}>
                    Coming Soon
                  </Badge>
                </Group>
                <Text fw={700} size="xl" c="#0a0a0a" mb={8}>Marketplace</Text>
                <Text size="sm" c="#64748b" lh={1.7} mb={20}>
                  One tap to install.
                </Text>
                <Group gap={8} wrap="wrap" style={{ marginTop: 'auto' }}>
                  {[
                    { icon: IconSearch, label: 'Discover' },
                    { icon: IconShare, label: 'Share' },
                    { icon: IconSparkles, label: 'Install' },
                  ].map((item) => (
                    <Badge
                      key={item.label}
                      variant="light"
                      color="gray"
                      size="md"
                      radius="md"
                      leftSection={<item.icon size={14} color="#7c3aed" stroke={1.5} />}
                      style={{ textTransform: 'none', fontWeight: 500, background: '#f1f5f9', color: '#475569', border: '1px solid #e2e8f0' }}
                    >
                      {item.label}
                    </Badge>
                  ))}
                </Group>
              </Box>
            </motion.div>
          </SimpleGrid>
        </Container>
      </Box>

      {/* ═══════════════════════════════════════════════════
          CHAPTER 5: TRUST BAR
          ═══════════════════════════════════════════════════ */}
      <Box component="section" py={{ base: 56, md: 80 }}>
        <Container size="lg">
          <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={VP} custom={0}>
            <Box
              style={{
                background: 'linear-gradient(135deg, #0a0a0a 0%, #1e1b4b 100%)',
                borderRadius: 20,
                padding: 'clamp(40px, 6vw, 64px)',
                position: 'relative',
                overflow: 'hidden',
              }}
            >
              <Box style={{ position: 'absolute', width: 400, height: 400, borderRadius: '50%', border: '1px solid rgba(124,58,237,.15)', top: -200, right: -100, animation: 'pulse-ring 8s ease-in-out infinite', pointerEvents: 'none' }} />
              <SimpleGrid cols={{ base: 2, sm: 4 }} spacing="xl">
                {STATS.map((s, i) => (
                  <motion.div key={s.label} variants={fadeUp} initial="hidden" whileInView="visible" viewport={VP} custom={i}>
                    <Stack gap={4} align="center">
                      <Text fw={800} style={{ fontSize: 'clamp(2.2rem, 5vw, 3.5rem)', lineHeight: 1, color: '#fff', letterSpacing: '-.02em' }}>{s.value}</Text>
                      <Text size="xs" fw={600} tt="uppercase" style={{ letterSpacing: '.12em', color: 'rgba(255,255,255,.4)' }}>{s.label}</Text>
                    </Stack>
                  </motion.div>
                ))}
              </SimpleGrid>
            </Box>
          </motion.div>
        </Container>
      </Box>

      {/* ═══════════════════════════════════════════════════
          CHAPTER 7: FAQ
          ═══════════════════════════════════════════════════ */}
      <Box component="section" py={{ base: 80, md: 120 }} style={{ borderTop: '1px solid rgba(0,0,0,.04)' }}>
        <Container size="md">
          <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={VP} custom={0}>
            <Stack align="center" ta="center" mb={48}>
              <SectionLabel>FAQ</SectionLabel>
              <SectionTitle>Frequently asked questions</SectionTitle>
            </Stack>
          </motion.div>

          <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={VP} custom={1}>
            <Accordion
              variant="separated"
              radius="lg"
              styles={{
                item: {
                  background: 'rgba(255,255,255,.7)',
                  backdropFilter: 'blur(8px)',
                  border: '1px solid rgba(0,0,0,.06)',
                  '&[data-active]': {
                    border: '1px solid rgba(124,58,237,.12)',
                  },
                },
                control: {
                  padding: '20px 24px',
                  '&:hover': {
                    background: 'transparent',
                  },
                },
                label: {
                  fontWeight: 600,
                  fontSize: 15,
                  color: '#0a0a0a',
                },
                panel: {
                  padding: '0 24px 20px',
                },
                content: {
                  padding: 0,
                },
                chevron: {
                  color: '#7c3aed',
                },
              }}
            >
              {FAQ_ITEMS.map((item, i) => (
                <Accordion.Item key={i} value={`faq-${i}`}>
                  <Accordion.Control>{item.q}</Accordion.Control>
                  <Accordion.Panel>
                    <Text size="sm" c="#64748b" lh={1.7}>
                      {item.a}
                      {item.link && (
                        <>
                          {' '}
                          <Text component={Link} to={item.link} size="sm" c="#7c3aed" fw={600} style={{ textDecoration: 'none' }}>
                            View pricing →
                          </Text>
                        </>
                      )}
                    </Text>
                  </Accordion.Panel>
                </Accordion.Item>
              ))}
            </Accordion>
          </motion.div>
        </Container>
      </Box>

      {/* ═══════════════════════════════════════════════════
          CHAPTER 8: CLOSING CTA
          ═══════════════════════════════════════════════════ */}
      <Box
        component="section"
        py={{ base: 100, md: 160 }}
        style={{ textAlign: 'center', position: 'relative', overflow: 'hidden' }}
      >
        <Box style={{ position: 'absolute', width: 800, height: 800, borderRadius: '50%', background: 'radial-gradient(circle, rgba(124,58,237,.04) 0%, transparent 60%)', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', pointerEvents: 'none' }} />

        <Container size="sm" style={{ position: 'relative', zIndex: 1 }}>
          <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={VP} custom={0}>
            <Title order={2} mb={16} style={{ fontSize: 'clamp(2.2rem, 5vw, 3.5rem)', fontWeight: 800, color: '#0a0a0a', letterSpacing: '-.03em', lineHeight: 1.1 }}>
              Manifest
              <br />
              <span className="hero-gradient-text">yourself.</span>
            </Title>
          </motion.div>

          <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={VP} custom={1}>
            <Text c="#64748b" mb={36} size="lg" maw={400} mx="auto" lh={1.7}>
              Let AI handle the rest. You have better things to do.
            </Text>
          </motion.div>

          <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={VP} custom={2}>
            <Button
              size="xl" color="dark" radius="xl" className="hero-cta"
              rightSection={<IconArrowRight size={20} />}
              onClick={() => { trackCTAClick('Start Building', 'final-cta', '/login'); go(); }}
              style={{ fontWeight: 600, fontSize: '1.05rem', paddingInline: 36, height: 56 }}
            >
              Start Building
            </Button>
          </motion.div>

          <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={VP} custom={3}>
            <Text size="xs" c="#94a3b8" mt={16}>
              Join the alpha {'\u2014'} your first Manifestant is free
            </Text>
          </motion.div>
        </Container>
      </Box>
    </Box>
  );
}
